/**
 * La apariencia de la página pública de una oferta: la pestaña «Apariencia» de
 * su ficha, en Cobros o en Reservas.
 *
 * ── Carpetas, como el editor ─────────────────────────────────────────────────
 * Las mismas carpetas que el panel de marca del editor —Logo, Paleta, Estilos de
 * texto, Botones—, y con su mismo gesto: acordeón. Se abre una en su sitio y las
 * demás siguen a la vista; al abrir otra, la anterior se cierra. Fue una pantalla
 * aparte con «volver», y ahí dejaban de verse las otras carpetas y lo que cada
 * una tenía cambiado.
 *
 * ── Elegir viendo ────────────────────────────────────────────────────────────
 * Los `<select>` del navegador no pueden enseñar un color ni una letra: elegir
 * «Superficie» o «H3» era elegir un nombre. Aquí cada opción se ve —la muestra
 * del color, el nombre de la tipografía escrito en esa tipografía, el botón en
 * pequeño, el logo como sale— y la página de al lado lo aplica al momento. Sólo
 * el nombre y la muestra: sin códigos, píxeles ni pesos, que no ayudan a elegir.
 *
 * ── La carpeta «Logo» ────────────────────────────────────────────────────────
 * Decide sólo qué se enseña de la marca en esta página, y lleva sólo ese
 * selector. Cambiar la marca en sí es cosa del editor.
 *
 * ── La vista previa ES la página ─────────────────────────────────────────────
 * El HTML lo pinta el worker con la misma función que sirve la página
 * (`page-preview`), y va en un iframe aislado: nada del panel entra. Lo único
 * que se hace aquí es ponerle encima lo que se está eligiendo, sin guardar, con
 * la MISMA función que usa el worker (`offerPageStyleCss`) y las tipografías por
 * el mismo proxy. El distintivo de véltica no sale: es de la página publicada.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClick,
  IconCopy,
  IconDroplet,
  IconExternalLink,
  IconEyeOff,
  IconLayoutBottombar,
  IconLayoutNavbar,
  IconLetterT,
  IconLoader2,
  IconPhoto,
  IconTrademark,
} from '@tabler/icons-react'
import {
  OFFER_PAGE_BRAND_MODES,
  ON_DARK_TEXT,
  googleFontSpecsFor,
  isDarkSurface,
  normalizeProjectTheme,
  siteTextColor,
  solidSurfaceColor,
  offerPageStyleCss,
  offerPageStyleFonts,
  readOfferPageStyle,
  type OfferPageBrandMode,
  type OfferPageStyle,
} from '@webbuilder/theme'

import { PendingChangesActions } from '../../DashboardTopbarActions'
import { useToast } from '../../Toast'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { WORKER_URL, getOfferPagePreview, updateOffer, type Offer } from '../../../lib/api'
import type { ProjectTheme } from '../../../lib/preview/types'
import { failureDetail, isFree } from './offerShared'

/** La hoja que se reescribe con cada cambio, dentro del marco. */
const LOOK_ID = 'veltica-look-preview'
/** El proxy de fuentes del worker. Ver `routes/fontProxy.ts`. */
const FONT_PROXY = `${WORKER_URL}/_veltica/fonts`
/* El marco se pinta a un ancho de escritorio y se reduce entero para caber:
   así se ve la planta de dos columnas publicada y no su versión de móvil. */
const FRAME_WIDTH = 1280
const FRAME_HEIGHT = 860

type Folder = 'brand' | 'colors' | 'type' | 'button'
type Brand = ReturnType<typeof normalizeProjectTheme>

/** El texto de cada modo del logo. Claves enteras: una armada no se puede buscar. */
const LOGO_MODE_KEY: Record<OfferPageBrandMode, string> = {
  '': 'offers.look.defaultOption',
  text: 'offers.look.logoMode.text',
  both: 'offers.look.logoMode.both',
  logo: 'offers.look.logoMode.logo',
  hide: 'offers.look.logoMode.hide',
}

/** Ver `tokenName` en `CatalogPageSettings`: el nombre escrito, o su clave. */
function tokenName(token: { id: string; name?: string | undefined; nameKey?: string | undefined }, t: (key: string) => string): string {
  return token.name || (token.nameKey ? t(token.nameKey) : token.id)
}

/**
 * Las variables del tema para `:root`, como las escribe `themeVarsCss` en el
 * worker: los colores base y los tokens con su propia variable, sin nada que
 * pueda salirse de la declaración.
 */
function themeVarsCss(theme: Brand): string {
  const clean = (value: unknown) => (typeof value === 'string' ? value.replace(/[<>{};\\]/g, '').trim() : '')
  const out: string[] = []
  for (const [key, value] of Object.entries(theme.colors)) {
    if (clean(value)) out.push(`--wb-${key}:${clean(value)}`)
  }
  for (const token of theme.colorComponents) {
    if (token.cssVar && /^--[A-Za-z0-9_-]{1,80}$/.test(token.cssVar) && clean(token.value)) out.push(`${token.cssVar}:${clean(token.value)}`)
  }
  return out.join(';')
}

/**
 * Los estilos de un botón para su miniatura: su fondo, su borde, su letra y su
 * radio, pero a tamaño de lista. Los tamaños del token son los de una página, y
 * dentro de un renglón de cuarenta píxeles no caben.
 */
export function buttonSampleStyle(styles: object | undefined): CSSProperties {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(styles ?? {})) {
    if (typeof value !== 'string' || value === '') continue
    if (/^(margin|padding|width|height|minHeight|minWidth|fontSize|lineHeight|letterSpacing)/.test(key)) continue
    out[key] = value
  }
  return out as CSSProperties
}

/**
 * Un desplegable que enseña lo que ofrece.
 *
 * Flota ENCIMA de lo de abajo en vez de empujarlo: abierto en línea, la carpeta
 * crecía cien o doscientos píxeles a cada selector y todo lo de debajo saltaba,
 * incluidas las otras carpetas. Se cierra al elegir, al pulsar fuera o con
 * Escape, como cualquier menú; abierto uno, los demás se cierran.
 */
export function LookPicker({ label, hideLabel = false, open, onToggle, onClose, current, options, selected, onPick }: {
  label: string
  /** El rótulo sólo para el lector de pantalla: cuando la carpeta ya lo dice. */
  hideLabel?: boolean | undefined
  open: boolean
  onToggle: () => void
  onClose: () => void
  /** Lo que se enseña cerrado: la opción elegida, con su muestra. */
  current: ReactNode
  options: { key: string; node: ReactNode }[]
  selected: string
  onPick: (key: string) => void
}) {
  const fieldRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!fieldRef.current?.contains(event.target as Node)) onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  return (
    <div className={`dash-look-field${open ? ' is-open' : ''}`} ref={fieldRef}>
      <span className={hideLabel ? 'dash-sr-only' : 'dash-look-field-label'}>{label}</span>
      <button type="button" className="dash-look-trigger" aria-expanded={open} aria-haspopup="listbox" onClick={onToggle}>
        {current}
        <IconChevronDown size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div className="dash-look-options" role="listbox" aria-label={label}>
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              role="option"
              aria-selected={option.key === selected}
              className={option.key === selected ? 'is-selected' : ''}
              onClick={() => onPick(option.key)}
            >
              {option.node}
              {option.key === selected ? <IconCheck size={14} aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function OfferAppearanceSettings({ projectId, token, offer, theme, siteOrigin, onChange }: {
  projectId: string
  token: string
  offer: Offer
  /** El tema del sitio: de aquí salen los tokens que se ofrecen y la marca. */
  theme?: ProjectTheme | undefined
  /** Sólo para la barra del marco. */
  siteOrigin?: string | null | undefined
  /** La oferta que devuelve el servidor al guardar; la lista vive fuera. */
  onChange: (offer: Offer) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()

  const [style, setStyle] = useState<OfferPageStyle>(() => readOfferPageStyle(offer.pageStyle))
  const [saved, setSaved] = useState<OfferPageStyle>(() => readOfferPageStyle(offer.pageStyle))
  const [saving, setSaving] = useState(false)
  /** La carpeta desplegada. Una sola: abrir otra cierra ésta. */
  const [openFolder, setOpenFolder] = useState<Folder | null>(null)
  /** El desplegable abierto dentro de la carpeta. Uno a la vez. */
  const [openField, setOpenField] = useState<string | null>(null)
  const closeField = useCallback(() => setOpenField(null), [])

  /* El tema normalizado igual que en el editor y en el worker. */
  const brand = useMemo(() => normalizeProjectTheme(theme ?? null), [theme])

  /*
   * Las tipografías que enseñan los desplegables, cargadas en el panel.
   *
   * Aquí sí por Google directamente, como el resto de pantallas de marca del
   * panel: es lo que ve quien administra, no lo que se sirve a sus visitantes.
   */
  const panelFontHrefs = useMemo(() => googleFontSpecsFor([
    brand.typography?.headingFont,
    brand.brandLogo?.nameFont,
    ...brand.typographyComponents.map((item) => item.styles.fontFamily),
    ...brand.buttonComponents.map((item) => item.styles.fontFamily),
  ]).map((spec) => `https://fonts.googleapis.com/css2?family=${spec}&display=swap`), [brand])

  useEffect(() => {
    for (const href of panelFontHrefs) {
      if (document.head.querySelector(`link[data-brand-font="${CSS.escape(href)}"]`)) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.dataset.brandFont = href
      document.head.appendChild(link)
    }
  }, [panelFontHrefs])

  /*
   * La página de verdad, pedida al worker.
   *
   * Otra vez cuando la oferta cambia (`updatedAt`): el nombre, la portada o el
   * precio salen de ella, y un marco con el nombre de antes haría dudar de todo
   * lo demás que enseña.
   */
  const [html, setHtml] = useState<string | null>(null)
  /* Si el sitio publicado tiene barra y pie: sin ellos, el interruptor explica
     que aparecerán al publicar en vez de no hacer nada visible. */
  const [siteChrome, setSiteChrome] = useState({ nav: false, footer: false })
  const [failure, setFailure] = useState('')
  useEffect(() => {
    let alive = true
    setFailure('')
    getOfferPagePreview(projectId, offer.id, token)
      .then((result) => {
        if (!alive) return
        setHtml(result.html)
        setSiteChrome({ nav: Boolean(result.hasSiteNav), footer: Boolean(result.hasSiteFooter) })
      })
      .catch((error: unknown) => { if (alive) setFailure(failureDetail(error)) })
    return () => { alive = false }
  }, [projectId, token, offer.id, offer.updatedAt])

  /* Una hoja vacía al final de la cabecera: la de lo elegido. Va DESPUÉS de la
     de la página, que es donde la pone el worker, para ganar por orden igual. */
  const srcDoc = useMemo(
    () => (html ? html.replace(/<\/head>/i, `<style id="${LOOK_ID}"></style></head>`) : ''),
    [html],
  )

  /* Lo elegido, escrito exactamente como lo escribe `offerPageLook` en el worker. */
  const lookCss = useMemo(() => {
    const css = offerPageStyleCss(brand, style, 'body')
    /* La barra y el pie del sitio vienen siempre en el marco; apagados, se
       esconden aquí para que el interruptor se vea al momento. */
    const chrome = [
      style.siteNav === 'hide' ? '.veltica-site-header{display:none !important}' : '',
      style.siteFooter === 'hide' ? '.veltica-site-footer{display:none !important}' : '',
    ].filter(Boolean).join('\n')
    return [css ? `:root{${themeVarsCss(brand)}}\n${css}` : '', chrome].filter(Boolean).join('\n')
  }, [brand, style])
  const fontHrefs = useMemo(
    () => googleFontSpecsFor(offerPageStyleFonts(brand, style)).map((spec) => `${FONT_PROXY}/css?family=${spec}&display=swap`),
    [brand, style],
  )

  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const applyLook = useCallback(() => {
    const doc = frameRef.current?.contentDocument
    if (!doc?.head) return
    const sheet = doc.getElementById(LOOK_ID)
    if (sheet) sheet.textContent = lookCss
    for (const href of fontHrefs) {
      if (doc.head.querySelector(`link[data-look-font="${CSS.escape(href)}"]`)) continue
      const link = doc.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      link.dataset.lookFont = href
      doc.head.appendChild(link)
    }
  }, [lookCss, fontHrefs])
  useEffect(() => { applyLook() }, [applyLook])

  /* El marco a escala del hueco que tenga, medido y no supuesto: la columna
     cambia de ancho con la ventana y con la barra lateral. */
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(0.5)
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const measure = () => setScale(Math.min(1, stage.clientWidth / FRAME_WIDTH))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(stage)
    return () => observer.disconnect()
  }, [html])

  const dirty = JSON.stringify(style) !== JSON.stringify(saved)
  const edit = (patch: Partial<OfferPageStyle>) => setStyle((current) => ({ ...current, ...patch }))

  const save = async () => {
    setSaving(true)
    try {
      const result = await updateOffer(projectId, offer.id, { pageStyle: style }, token)
      onChange(result)
      /* Lo que conteste el servidor, que es lo que se guardó de verdad. */
      const next = readOfferPageStyle(result.pageStyle)
      setStyle(next)
      setSaved(next)
      showToast(t('offers.look.saved'))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    } finally {
      setSaving(false)
    }
  }

  /* Las tres puertas: la gratuita no enseña importe —es un formulario—, y la de
     un servicio con agenda es la de reservar, con su etiqueta «Reservar». */
  const free = isFree(offer)
  const bookable = offer.booking.durationMinutes !== null
  const publicPath = bookable ? '/book/' : free ? '/checkout/free/' : '/checkout/buy/'
  const host = `${siteOrigin ? siteOrigin.replace(/^https?:\/\//, '') : ''}${publicPath}${offer.slug}`
  /* La dirección entera, para copiarla y abrirla desde la barra del marco: es
     donde se lee, así que es donde se espera poder hacer las dos cosas. */
  const url = siteOrigin ? `${siteOrigin}${publicPath}${offer.slug}` : ''
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      showToast(t('common.linkCopied'))
    } catch {
      showToast(t('common.copyFailed'), 'error')
    }
  }

  /** Qué campos lleva cada carpeta en ESTA oferta. */
  const folderFields: Record<Folder, (keyof OfferPageStyle)[]> = {
    brand: ['brand'],
    colors: ['background', 'panel', 'text', ...(free || bookable ? ['badge' as const] : [])],
    type: ['title', 'heading', 'body', ...(free ? [] : ['price' as const])],
    button: ['button'],
  }
  const folders: { id: Folder; labelKey: string; icon: ReactNode }[] = [
    { id: 'brand', labelKey: 'brand.logo', icon: <IconTrademark size={16} /> },
    { id: 'colors', labelKey: 'brand.palette', icon: <IconDroplet size={16} /> },
    { id: 'type', labelKey: 'brand.textStyles', icon: <IconLetterT size={16} /> },
    { id: 'button', labelKey: 'brand.buttonStyles', icon: <IconClick size={16} /> },
  ]
  /* La barra y el pie del sitio, cada uno en su tarjeta y con su interruptor
     a la vista: no hay nada que elegir dentro, sólo si esta página los lleva.
     Van en las tres páginas —comprar, reclamar y reservar—. */
  const chromeCards = [
    { field: 'siteNav' as const, labelKey: 'offers.look.siteNav', icon: <IconLayoutNavbar size={16} />, published: siteChrome.nav },
    { field: 'siteFooter' as const, labelKey: 'offers.look.siteFooter', icon: <IconLayoutBottombar size={16} />, published: siteChrome.footer },
  ]

  /* Un id guardado que ya no está en el tema se enseña como la opción vacía:
     es lo que se pinta, y un desplegable que dice otra cosa que la vista previa
     no se puede creer. */
  const known = (list: { id: string }[], id: string) => (list.some((item) => item.id === id) ? id : '')
  const toggle = (field: string) => setOpenField((current) => (current === field ? null : field))
  const pick = (field: keyof OfferPageStyle, value: string) => {
    edit({ [field]: value } as Partial<OfferPageStyle>)
    setOpenField(null)
  }
  /* Abrir otra carpeta cierra la anterior y cualquier desplegable que quedara
     abierto dentro de ella. */
  const toggleFolder = (id: Folder) => {
    setOpenField(null)
    setOpenFolder((current) => (current === id ? null : id))
  }

  const asTodayNode = (sample: ReactNode) => (
    <span className="dash-look-option">
      {sample}
      <span className="dash-look-option-copy"><strong>{t('offers.look.defaultOption')}</strong></span>
    </span>
  )

  /* ── Colores: la muestra y el nombre ──────────────────────────────────────
     Sin el valor (`#1c1c1e`): quien elige aquí elige un color de su marca por
     su nombre y su muestra, no por su código. */
  const colorNode = (item: Brand['colorComponents'][number]) => (
    <span className="dash-look-option">
      <span className="dash-look-swatch" style={{ background: item.value }} aria-hidden="true" />
      <span className="dash-look-option-copy">
        <strong>{tokenName(item, t)}</strong>
      </span>
    </span>
  )
  const colorField = (field: 'background' | 'panel' | 'text' | 'badge', labelKey: string) => {
    const value = known(brand.colorComponents, style[field])
    const selected = brand.colorComponents.find((item) => item.id === value)
    /* La etiqueta de «Gratis»/«Reservar» sí tiene un color por defecto que se
       puede enseñar —el apagado de la marca—, así que su «Predeterminado» lleva
       ESE color y no la muestra mitad clara mitad oscura. Los otros tres
       —fondo, columna, texto— siguen con ella: ahí el valor de verdad depende de
       la página, y pintar uno concreto sería prometer un color que no es. */
    const autoSwatch = field === 'badge'
      ? <span className="dash-look-swatch" style={{ background: brand.colors.muted }} aria-hidden="true" />
      : <span className="dash-look-swatch is-auto" aria-hidden="true" />
    return (
      <LookPicker
        key={field}
        label={t(labelKey)}
        open={openField === field}
        onToggle={() => toggle(field)}
        onClose={closeField}
        current={selected ? colorNode(selected) : asTodayNode(autoSwatch)}
        selected={value}
        onPick={(key) => pick(field, key)}
        options={[
          { key: '', node: asTodayNode(autoSwatch) },
          ...brand.colorComponents.map((item) => ({ key: item.id, node: colorNode(item) })),
        ]}
      />
    )
  }

  /* ── Textos: el nombre, escrito en su tipografía ─────────────────────────
     Sin la ficha técnica (familia · peso · tamaño): la muestra «Aa» y el nombre
     en su propia letra ya enseñan cómo es, sin leer cifras. */
  const typeNode = (item: Brand['typographyComponents'][number]) => {
    const family = item.styles.fontFamily
    return (
      <span className="dash-look-option">
        <span className="dash-look-type-sample" style={{ fontFamily: family, fontWeight: item.styles.fontWeight }} aria-hidden="true">Aa</span>
        <span className="dash-look-option-copy">
          <strong style={{ fontFamily: family }}>{tokenName(item, t)}</strong>
        </span>
      </span>
    )
  }
  const typographyField = (field: 'title' | 'heading' | 'body' | 'price', labelKey: string) => {
    const value = known(brand.typographyComponents, style[field])
    const selected = brand.typographyComponents.find((item) => item.id === value)
    const autoSample = <span className="dash-look-type-sample is-auto" aria-hidden="true">Aa</span>
    return (
      <LookPicker
        key={field}
        label={t(labelKey)}
        open={openField === field}
        onToggle={() => toggle(field)}
        onClose={closeField}
        current={selected ? typeNode(selected) : asTodayNode(autoSample)}
        selected={value}
        onPick={(key) => pick(field, key)}
        options={[
          { key: '', node: asTodayNode(autoSample) },
          ...brand.typographyComponents.map((item) => ({ key: item.id, node: typeNode(item) })),
        ]}
      />
    )
  }

  /* ── Botones: el botón en pequeño, con sus colores, su borde y su letra ──── */
  const buttonNode = (item: Brand['buttonComponents'][number]) => (
    <span className="dash-look-option">
      <span className="dash-look-btn-sample" style={buttonSampleStyle(item.styles)} aria-hidden="true">Aa</span>
      <span className="dash-look-option-copy">
        <strong>{tokenName(item, t)}</strong>
      </span>
    </span>
  )
  const buttonField = () => {
    const value = known(brand.buttonComponents, style.button)
    const selected = brand.buttonComponents.find((item) => item.id === value)
    /* Un botón de verdad, no un contorno de puntos.
     *
     * El «Predeterminado» es una opción más de la lista y tiene que poder
     * compararse con las otras de un vistazo: al lado de tres botones rellenos,
     * un rectángulo discontinuo se leía como un hueco vacío y no como el aspecto
     * que la página va a tener si no se toca nada.
     *
     * Relleno con el apagado de la marca —el color con el que sale— y su letra
     * calculada encima, que es lo que evita un texto ilegible cuando ese token
     * es oscuro. */
    /* El token puede venir como `var(--wb-…)` o como un hex: `solidSurfaceColor`
       lo resuelve contra el tema y devuelve el color de verdad, que es lo que
       `isDarkSurface` sabe leer. */
    const autoSurface = solidSurfaceColor(brand.colors.muted, brand)
    const autoInk = autoSurface && isDarkSurface(autoSurface) ? ON_DARK_TEXT : siteTextColor(brand)
    const autoSample = (
      <span
        className="dash-look-btn-sample is-auto"
        style={{ background: brand.colors.muted, color: autoInk }}
        aria-hidden="true"
      >
        Aa
      </span>
    )
    return (
      <LookPicker
        label={t('catalogPage.button')}
        open={openField === 'button'}
        onToggle={() => toggle('button')}
        onClose={closeField}
        current={selected ? buttonNode(selected) : asTodayNode(autoSample)}
        selected={value}
        onPick={(key) => pick('button', key)}
        options={[
          { key: '', node: asTodayNode(autoSample) },
          ...brand.buttonComponents.map((item) => ({ key: item.id, node: buttonNode(item) })),
        ]}
      />
    )
  }

  /* ── Logo: qué se ve de la marca del sitio en esta página ─────────────────
     Cada opción se enseña como saldría —la imagen, el nombre o los dos— y dice
     cuándo no puede cumplirse: «Solo logo» en una marca sin imagen no pinta
     nada, y es mejor saberlo aquí que descubrirlo en la página. */
  const brandLogo = brand.brandLogo
  const logoImage = brandLogo?.imageLight ?? ''
  const brandName = brandLogo?.showName === false ? '' : (brandLogo?.name ?? '').trim()
  const nameStyle: CSSProperties = { fontFamily: brandLogo?.nameFont || brand.typography?.headingFont }
  /** Qué se ve con cada modo, con la marca de hoy. */
  const lockupFor = (mode: OfferPageBrandMode) => ({
    image: (mode === '' || mode === 'logo' || mode === 'both') && logoImage ? logoImage : '',
    name: (mode === 'text' || mode === 'both') ? brandName : '',
  })
  const logoModeNode = (mode: OfferPageBrandMode) => {
    const lockup = lockupFor(mode)
    const missing = mode === 'logo' && !logoImage
      ? t('offers.look.noLogoImage')
      : mode === 'text' && !brandName
        ? t('offers.look.noBrandName')
        : ''
    return (
      <span className="dash-look-option">
        <span className={`dash-look-logo-sample${mode === 'hide' ? ' is-hidden' : ''}`} aria-hidden="true">
          {mode === 'hide'
            ? <IconEyeOff size={15} />
            : lockup.image || lockup.name
              ? (
                <>
                  {lockup.image ? <img src={lockup.image} alt="" /> : null}
                  {lockup.name ? <b style={nameStyle}>{lockup.name}</b> : null}
                </>
              )
              : <IconPhoto size={15} />}
        </span>
        <span className="dash-look-option-copy">
          <strong>{t(LOGO_MODE_KEY[mode])}</strong>
          {missing ? <small>{missing}</small> : null}
        </span>
      </span>
    )
  }
  /* Sólo el selector. Hubo una tarjeta con la marca en grande, un enlace al
     editor para cambiarla y una nota explicándolo: la carpeta decía tres veces
     lo mismo que ya enseña cada opción del desplegable —la imagen, el nombre o
     los dos, como saldrían—. El rótulo va sólo para el lector de pantalla: con
     un único mando en la carpeta, su nombre ya lo dice la carpeta. */
  const brandContent = (
    <LookPicker
      label={t('offers.look.brandShow')}
      hideLabel
      open={openField === 'brand'}
      onToggle={() => toggle('brand')}
      onClose={closeField}
      current={logoModeNode(style.brand)}
      selected={style.brand}
      onPick={(key) => pick('brand', key)}
      options={OFFER_PAGE_BRAND_MODES.map((mode) => ({ key: mode, node: logoModeNode(mode) }))}
    />
  )

  const folderContent: Record<Folder, ReactNode> = {
    brand: brandContent,
    colors: (
      <>
        {colorField('background', 'catalogPage.background')}
        {colorField('panel', 'offers.look.panel')}
        {colorField('text', 'catalogPage.text')}
        {/* La etiqueta de la columna: «Gratis» en la de reclamar y
            «Reservar» en la de reservar. La de compra no lleva ninguna. */}
        {free || bookable ? colorField('badge', bookable ? 'offers.look.badgeBook' : 'offers.look.badge') : null}
      </>
    ),
    type: (
      <>
        {typographyField('title', 'offers.look.title')}
        {typographyField('heading', 'offers.look.heading')}
        {typographyField('body', 'offers.look.body')}
        {free ? null : typographyField('price', 'offers.look.price')}
      </>
    ),
    button: buttonField(),
  }

  return (
    <div className="dash-offer-look">
      <PendingChangesActions
        dirty={dirty}
        saving={saving}
        onDiscard={() => setStyle(saved)}
        onSave={() => { void save() }}
      />

      <div className="dash-catalog-editor">
        <aside className="dash-look-panel" aria-label={t('offers.detail.tab.look')}>
          <p className="dash-look-eyebrow">{t('brand.customize')}</p>
          <div className="dash-look-folders">
            {folders.map((item) => {
              const count = folderFields[item.id].filter((field) => style[field]).length
              const open = openFolder === item.id
              return (
                <div key={item.id} className={`dash-look-folder-card${open ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="dash-look-folder"
                    aria-expanded={open}
                    aria-controls={`look-folder-${item.id}`}
                    onClick={() => toggleFolder(item.id)}
                  >
                    <span className="dash-widget-glass-icon dash-look-folder-icon" aria-hidden="true">{item.icon}</span>
                    <span className="dash-look-folder-copy">
                      <strong>{t(item.labelKey)}</strong>
                      {/* El mismo gris para los dos estados: es un dato de la
                          carpeta, no un aviso, y en azul competía con el nombre. */}
                      <small>
                        {count > 0 ? t('offers.look.customized', { count }) : t('offers.look.asToday')}
                      </small>
                    </span>
                    <IconChevronRight size={16} aria-hidden="true" />
                  </button>
                  {open ? (
                    <div className="dash-look-folder-body" id={`look-folder-${item.id}`}>
                      {folderContent[item.id]}
                    </div>
                  ) : null}
                </div>
              )
            })}
            {chromeCards.map((card) => {
              const on = style[card.field] !== 'hide'
              return (
                <div key={card.field} className="dash-look-folder-card">
                  <div className="dash-look-folder is-switch">
                    <span className="dash-widget-glass-icon dash-look-folder-icon" aria-hidden="true">{card.icon}</span>
                    <span className="dash-look-folder-copy">
                      <strong>{t(card.labelKey)}</strong>
                      <small>{!on ? t('offers.look.siteOff') : card.published || !html ? t('offers.look.siteOn') : t('offers.look.siteMissing')}</small>
                    </span>
                    <button
                      type="button"
                      className={`dash-page-settings-switch${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      aria-label={t(card.labelKey)}
                      onClick={() => edit({ [card.field]: on ? 'hide' : '' } as Partial<OfferPageStyle>)}
                    >
                      <i />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </aside>

        <div className="dash-catalog-stage">
          <div className="dash-catalog-browser">
            {/* La barra, con sus mandos. Sin `aria-hidden` en el renglón: con
                botones dentro, esconderlo entero los dejaba fuera del alcance de
                un lector de pantalla. Los tres puntos, que sí son adorno, lo
                llevan uno a uno.

                Sólo con la oferta publicada: en borrador el enlace da un 404 y
                copiarlo o abrirlo no lleva a ninguna parte. Aquí no va el botón
                de activar que sí tiene la página del catálogo — publicar una
                oferta es una llamada al servidor que Stripe puede rechazar, y
                eso no cabe en una barra decorativa. */}
            <div className="dash-catalog-browser-bar">
              <i aria-hidden="true" /><i aria-hidden="true" /><i aria-hidden="true" />
              <span>{host}</span>
              <div className="dash-catalog-browser-actions">
                {offer.status === 'active' && url ? (
                  <>
                    <button type="button" className="dash-secondary-btn" onClick={() => void copyLink()}>
                      <IconCopy size={13} aria-hidden="true" /> {t('bookings.page.copyLink')}
                    </button>
                    <a className="dash-secondary-btn" href={url} target="_blank" rel="noreferrer">
                      <IconExternalLink size={13} aria-hidden="true" /> {t('bookings.page.open')}
                    </a>
                  </>
                ) : null}
              </div>
            </div>
            <div ref={stageRef} style={{ position: 'relative', overflow: 'hidden', height: html ? FRAME_HEIGHT * scale : undefined }}>
              {failure ? (
                <div className="dash-placeholder dash-placeholder--inset"><span>{failure}</span></div>
              ) : !html ? (
                <div className="dash-placeholder dash-placeholder--inset"><IconLoader2 size={20} className="dash-spin" /></div>
              ) : (
                <iframe
                  ref={frameRef}
                  title={t('offers.detail.tab.look')}
                  /* Sin scripts ni formularios: es un dibujo. `allow-same-origin`
                     sólo para poder reescribir la hoja de lo elegido desde aquí. */
                  sandbox="allow-same-origin"
                  srcDoc={srcDoc}
                  onLoad={applyLook}
                  tabIndex={-1}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: FRAME_WIDTH,
                    height: FRAME_HEIGHT,
                    border: 0,
                    transform: `scale(${scale})`,
                    transformOrigin: '0 0',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </div>
          <p className="dash-catalog-stage-note">{t('offers.look.note')}</p>
        </div>
      </div>
    </div>
  )
}
